import { useState } from "react";

/**
 * Bannière de résultat d'un lien de paiement adhérent — champ en lecture seule,
 * bouton Copier, et (optionnel) bouton Régénérer.
 *
 * Partagée entre la page Paiements et le panneau Nouveau membre, où elle était
 * dupliquée quasi à l'identique.
 */
const CheckoutLinkResult = ({
  memberName,
  url,
  amount,
  expiresAt,
  title,
  onClose,
  onRegenerate,
  regenerating = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // expiresAt : timestamp Unix (secondes) OU chaîne ISO.
  const expiryDate = expiresAt
    ? typeof expiresAt === "number"
      ? new Date(expiresAt * 1000)
      : new Date(expiresAt)
    : null;
  const expiryLabel =
    expiryDate && !Number.isNaN(expiryDate.getTime())
      ? expiryDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
      : null;

  return (
    <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-5 py-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {title || `Lien de paiement — ${memberName}`}
          </p>
          <p className="text-xs text-slate-500">
            {amount != null && (
              <>{Number(amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</>
            )}
            {amount != null && expiryLabel && " · "}
            {expiryLabel && <>valable jusqu'au {expiryLabel}</>}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1 flex-shrink-0"
            aria-label="Fermer"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          className="flex-1 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-600 truncate focus:outline-none"
        />
        <button
          type="button"
          onClick={handleCopy}
          className="px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all flex-shrink-0"
          style={{ backgroundColor: copied ? "#059669" : "#111827" }}
        >
          {copied ? "Copié ✓" : "Copier"}
        </button>
      </div>

      {onRegenerate && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={regenerating}
          className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-50"
        >
          {regenerating ? "Régénération…" : "↻ Régénérer le lien (invalide l'ancien)"}
        </button>
      )}
    </div>
  );
};

export default CheckoutLinkResult;
